import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, Send, Users, TrendingUp, Loader2, Info } from 'lucide-react'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'

interface PushStats {
  activeSubscribers: number
  totalSubscribers: number
  todaySubscribers: number
  campaigns: Array<{
    id: number; title: string; body: string; url: string
    targeted: number; sent: number; failed: number; clicks: number
    status: string; created_at: string
  }>
}

export function Push() {
  const qc = useQueryClient()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [url, setUrl] = useState('https://receitaecia.com/')
  const [icon, setIcon] = useState('')

  const { data: stats, isLoading } = useQuery<PushStats>({
    queryKey: ['push-stats'],
    queryFn: () => api.get('/push/stats'),
    refetchInterval: 15000,
  })

  const send = useMutation({
    mutationFn: () => api.post('/push/send', { title, body, url, icon }),
    onSuccess: (r: any) => {
      toast.success(`Push enviado para ${r.targeted} inscritos!`)
      setTitle(''); setBody('')
      qc.invalidateQueries({ queryKey: ['push-stats'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Erro ao enviar'),
  })

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Bell className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Push Notifications</h1>
          <p className="text-sm text-muted-foreground">Reengaje sua audiência no navegador — sem janela de 24h, sem Meta</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> Inscritos ativos</div>
          <div className="mt-1 text-2xl font-bold text-emerald-500">{isLoading ? '—' : stats?.activeSubscribers.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> Hoje</div>
          <div className="mt-1 text-2xl font-bold">+{isLoading ? '—' : stats?.todaySubscribers.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> Total histórico</div>
          <div className="mt-1 text-2xl font-bold text-muted-foreground">{isLoading ? '—' : stats?.totalSubscribers.toLocaleString()}</div>
        </div>
      </div>

      {/* Compose */}
      <div className="rounded-xl border bg-card p-5 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold"><Send className="h-4 w-4" /> Disparar push</div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Título</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Receita nova no ar! 🍰" className="mt-1" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Texto (opcional)</Label>
          <Input value={body} onChange={e => setBody(e.target.value)} placeholder="Toque para ver a receita de hoje" className="mt-1" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Link ao clicar</Label>
            <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." className="mt-1" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Ícone (opcional)</Label>
            <Input value={icon} onChange={e => setIcon(e.target.value)} placeholder="https://.../icon.png" className="mt-1" />
          </div>
        </div>
        <div className="flex items-center justify-between pt-1">
          <p className="text-[11px] text-muted-foreground">Vai para {stats?.activeSubscribers ?? 0} inscritos ativos</p>
          <Button onClick={() => send.mutate()} disabled={send.isPending || !title || !url || !stats?.activeSubscribers}>
            {send.isPending ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Enviando...</> : <><Send className="mr-1 h-4 w-4" /> Disparar</>}
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>Os inscritos são capturados automaticamente quando alguém clica no botão do funil (Card de Captura). Quanto mais tráfego passa pelo funil, mais inscritos você acumula — permanentes e recirculáveis.</span>
      </div>

      {/* Campaigns history */}
      {stats?.campaigns && stats.campaigns.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold">Histórico de disparos</div>
          {stats.campaigns.map(c => (
            <div key={c.id} className="rounded-lg border bg-card p-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="truncate font-medium">{c.title}</div>
                  <div className="truncate text-xs text-muted-foreground">{c.body} → {c.url}</div>
                </div>
                <div className="flex shrink-0 items-center gap-3 text-[11px]">
                  <span className="text-emerald-500">{c.sent} enviados</span>
                  {c.failed > 0 && <span className="text-amber-500">{c.failed} falhas</span>}
                  <span className="text-primary">{c.clicks} cliques</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
